import { useContext, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { FaEnvelope } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import { get, post } from '../../utils/api';

/*
 * "Your email isn't verified yet — [Verify now]".
 *
 * Shown to signed-in job-seekers whose email was never verified: older accounts,
 * and assistant sign-ups that never finished. They are no longer stopped at
 * login (auth.controller.js signin); this is how they are asked instead. New
 * registrations verify before the account exists, so they never see it.
 *
 * Mounted in both headers, so it appears on every page. The status is fetched
 * once per signed-in user and cached here, so moving between pages does not
 * re-ask the server. "Later" hides it until the browser tab is closed.
 */

const STATUS_URL = '/api/me/email-verification';
const SNOOZE_KEY = 'emailVerifyBannerSnoozed';

// userId -> { verified, email }, shared by every mounted banner.
const cache = new Map();
const listeners = new Set();
const publish = () => listeners.forEach((fn) => fn());

const messageOf = (err, fallback) => err?.response?.data?.responseMessage || fallback;

const readSnooze = () => {
    try { return sessionStorage.getItem(SNOOZE_KEY) === '1'; } catch { return false; }
};

const EmailVerifyBanner = () => {
    const { isAuthenticated, userRole, currentUser } = useContext(AuthContext);
    const roleName = typeof userRole === 'string' ? userRole : userRole?.name;
    const userId = currentUser?.id;
    const applies = isAuthenticated && roleName === 'jobseeker' && Boolean(userId);

    const [, setTick] = useState(0);
    const [snoozed, setSnoozed] = useState(readSnooze);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fn = () => setTick((t) => t + 1);
        listeners.add(fn);
        return () => { listeners.delete(fn); };
    }, []);

    useEffect(() => {
        if (!applies || cache.has(userId)) return;
        cache.set(userId, null); // in flight
        get(STATUS_URL)
            .then((res) => {
                cache.set(userId, {
                    verified: Boolean(res?.responseData?.verified),
                    email: res?.responseData?.email || '',
                });
            })
            // Unknown status shows nothing: a banner on a guess is worse than none.
            .catch(() => cache.set(userId, { verified: true, email: '' }))
            .finally(publish);
    }, [applies, userId]);

    const status = applies ? cache.get(userId) : null;
    if (!status || status.verified) return null;

    const markVerified = () => {
        cache.set(userId, { ...status, verified: true });
        publish();
    };

    const snooze = () => {
        try { sessionStorage.setItem(SNOOZE_KEY, '1'); } catch { /* private mode */ }
        setSnoozed(true);
    };

    return (
        <>
            {!snoozed && (
                <div className="bg-amber-50 border-b border-amber-200 text-amber-900" role="status">
                    <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <FaEnvelope className="text-amber-600 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 min-w-[200px]">
                            Your email <strong className="font-semibold break-all">{status.email}</strong> isn't verified yet.
                            Verify it so schools can reach you.
                        </span>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="px-3 py-1 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700"
                        >
                            Verify now
                        </button>
                        <button type="button" onClick={snooze} className="px-2 py-1 text-amber-800 hover:underline">
                            Later
                        </button>
                    </div>
                </div>
            )}
            {open && (
                <VerifyDialog
                    email={status.email}
                    onClose={() => setOpen(false)}
                    onVerified={() => { setOpen(false); markVerified(); }}
                />
            )}
        </>
    );
};

/*
 * Sends the OTP the moment it opens — the person has just pressed "Verify
 * now", so making them press "Send" as well would be a step with no purpose.
 */
const VerifyDialog = ({ email, onClose, onVerified }) => {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const send = async () => {
        setError('');
        try {
            const res = await post(`${STATUS_URL}/send`, {});
            if (res?.responseData?.verified) { onVerified(); return; }
            setInfo(res?.responseMessage || `OTP sent to ${email}.`);
            setCooldown(res?.responseData?.retryAfterSeconds || 45);
        } catch (err) {
            const wait = err?.response?.data?.responseData?.retryAfterSeconds;
            if (wait) setCooldown(wait);
            setError(messageOf(err, 'Could not send the OTP. Please try again.'));
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { send(); }, []);

    const confirm = async () => {
        if (!/^\d{6}$/.test(otp)) { setError('Enter the 6-digit OTP.'); return; }
        setBusy(true);
        setError('');
        try {
            const res = await post(`${STATUS_URL}/verify`, { otp });
            if (res?.responseData?.verified) {
                onVerified();
                return;
            }
            setError(res?.responseMessage || 'Could not verify the OTP.');
        } catch (err) {
            setError(messageOf(err, 'Could not verify the OTP. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                Verify your email
                <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <p className="text-sm text-gray-600 mb-3">
                    {info || <>Sending a 6-digit OTP to <strong>{email}</strong>…</>}
                </p>
                <div className="flex gap-2">
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
                        maxLength={6}
                        placeholder="6-digit OTP"
                        aria-label="OTP"
                        autoFocus
                        className={`flex-1 min-w-0 px-3 py-2 border rounded-lg text-center tracking-[0.4em] font-bold
                            focus:outline-none focus:border-talexo-blue focus:ring-2 focus:ring-talexo-blue/20
                            ${error ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    />
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={busy || otp.length !== 6}
                        className="px-4 rounded-lg text-sm font-semibold text-white bg-talexo-blue hover:bg-talexo-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? 'Checking…' : 'Verify'}
                    </button>
                </div>
                {error && <p className="text-red-500 text-xs mt-1" role="alert">{error}</p>}
                <p className="text-xs text-gray-500 mt-3 mb-1">
                    Didn't get it? Check your spam folder, or{' '}
                    {cooldown > 0 ? (
                        <span>resend in {cooldown}s</span>
                    ) : (
                        <button type="button" onClick={send} className="text-talexo-blue font-medium hover:text-talexo-blue-dark">
                            resend the OTP
                        </button>
                    )}
                    .
                </p>
            </DialogContent>
        </Dialog>
    );
};

export default EmailVerifyBanner;
